import { useMemo } from "react";

/**
 * Hook personnalisé pour gérer les URLs des photos
 * @returns {Object} Méthodes pour manipuler les URLs de photos
 */
export const usePhotoUrl = () => {
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  // Utilisation de useMemo pour éviter de recréer les fonctions à chaque rendu
  return useMemo(() => {
    /**
     * Construit l'URL complète pour une photo basée sur le chemin stocké
     * @param {string} photoPath Le chemin de la photo tel que stocké dans la base de données
     * @param {string} defaultImagePath Image par défaut à utiliser si photoPath est vide (optionnel)
     * @returns {string} L'URL complète de l'image
     */
    const getPhotoUrl = (
      photoPath,
      defaultImagePath = "assets/icons/logo.svg"
    ) => {
      if (!photoPath) {
        return defaultImagePath; // Image par défaut
      }
      if (photoPath.startsWith("http")) {
        return photoPath;
      }
      let cleanBase = apiBaseUrl;
      if (cleanBase.endsWith("/api")) {
        cleanBase = cleanBase.slice(0, -4);
      }
      if (photoPath.startsWith("/")) {
        return `${cleanBase}${photoPath}`;
      }
      if (photoPath.startsWith("assets")) {
        return `${cleanBase}/${photoPath}`;
      }
      if (photoPath.startsWith("storage")) {
        return `${cleanBase}/${photoPath}`;
      }
      return `${cleanBase}/storage/${photoPath}`;
    };

    /**
     * Détermine si une URL d'image est une URL absolue (http/https)
     * @param {string} url L'URL à vérifier
     * @returns {boolean} indiquant si c'est une URL absolue
     */
    const isAbsoluteUrl = (url) => {
      return url?.startsWith("http://") || url?.startsWith("https://");
    };

    /**
     * Retourne l'URL de base de l'API
     * @returns {string} L'URL de base de l'API
     */
    const getApiBaseUrl = () => {
      return apiBaseUrl;
    };

    return {
      getPhotoUrl,
      isAbsoluteUrl,
      getApiBaseUrl,
    };
  }, [apiBaseUrl]);
};
